import { useEffect, useState } from "react";
import { Plus, Trash2, ChevronUp, ChevronDown, Save } from "lucide-react";
import { toast } from "sonner";
import { api, adminApi } from "../../lib/api";
import { useCrud } from "./useCrud";
import { Field } from "./ImageField";

const RowActions = ({ onSave, onUp, onDown, onDelete }) => (
  <div className="flex items-center gap-2">
    <button onClick={onSave} className="inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest bg-teal/90 text-obsidian px-4 py-2 hover:bg-teal"><Save className="h-4 w-4" /> Save</button>
    <button onClick={onUp} className="p-2 border border-[#2A201A] hover:border-teal hover:text-teal transition-colors"><ChevronUp className="h-4 w-4" /></button>
    <button onClick={onDown} className="p-2 border border-[#2A201A] hover:border-teal hover:text-teal transition-colors"><ChevronDown className="h-4 w-4" /></button>
    <button onClick={onDelete} className="ml-auto p-2 border border-[#2A201A] text-[#8F877C] hover:border-destructive hover:text-destructive transition-colors"><Trash2 className="h-4 w-4" /></button>
  </div>
);

const Textarea = ({ label, value, onChange, rows = 3 }) => (
  <div>
    <p className="font-mono text-[10px] uppercase tracking-widest text-[#8F877C] mb-2">{label}</p>
    <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows}
      className="w-full bg-obsidian border border-[#2A201A] px-3 py-2 text-sm text-bone focus:outline-none focus:border-teal resize-none" />
  </div>
);

const Header = ({ title, hint, onAdd, addLabel, testid }) => (
  <div className="flex justify-between items-center">
    <div>
      <h3 className="font-display text-2xl font-light text-bone">{title}</h3>
      {hint && <p className="text-[#8F877C] text-sm mt-1">{hint}</p>}
    </div>
    {onAdd && (
      <button onClick={onAdd} data-testid={testid} className="inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest bg-teal text-obsidian px-4 py-2.5 hover:bg-bone transition-colors"><Plus className="h-4 w-4" /> {addLabel}</button>
    )}
  </div>
);

export const PenalTermsEditor = () => {
  const { items, patch, add, save, remove, move } = useCrud("penal-terms", { term: "New term", definition: "" });
  return (
    <div className="space-y-4">
      <Header title="Defined Terms (Title I)" hint="Terms and their definitions." onAdd={add} addLabel="Add Term" testid="penal-term-add" />
      {items.map((it, idx) => (
        <div key={it.id} data-testid={`penal-term-edit-${idx}`} className="border border-[#2A201A] bg-surface/40 p-5 space-y-4">
          <Field label="Term" value={it.term} onChange={(v) => patch(it.id, "term", v)} />
          <Textarea label="Definition" value={it.definition} onChange={(v) => patch(it.id, "definition", v)} rows={3} />
          <RowActions onSave={() => save(it)} onUp={() => move(idx, -1)} onDown={() => move(idx, 1)} onDelete={() => remove(it.id)} />
        </div>
      ))}
    </div>
  );
};

export const AggravatingEditor = () => {
  const { items, patch, add, save, remove, move } = useCrud("penal-aggravating", { code: "X", circ: "New circumstance", description: "", fine: "", jail: "" });
  return (
    <div className="space-y-4">
      <Header title="Aggravating Circumstances" hint="The add-on modifiers table." onAdd={add} addLabel="Add Row" testid="penal-aggr-add" />
      {items.map((it, idx) => (
        <div key={it.id} data-testid={`penal-aggr-edit-${idx}`} className="border border-[#2A201A] bg-surface/40 p-5 space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Code" value={it.code} onChange={(v) => patch(it.id, "code", v)} />
            <Field label="Fine Added" value={it.fine} onChange={(v) => patch(it.id, "fine", v)} />
            <Field label="Jail Added" value={it.jail} onChange={(v) => patch(it.id, "jail", v)} />
          </div>
          <Field label="Circumstance" value={it.circ} onChange={(v) => patch(it.id, "circ", v)} />
          <Textarea label="Description" value={it.description} onChange={(v) => patch(it.id, "description", v)} rows={2} />
          <RowActions onSave={() => save(it)} onUp={() => move(idx, -1)} onDown={() => move(idx, 1)} onDelete={() => remove(it.id)} />
        </div>
      ))}
    </div>
  );
};

export const ControlledEditor = () => {
  const { items, patch, add, save, remove, move } = useCrud("penal-controlled", { item: "New item", permitted: "" });
  return (
    <div className="space-y-4">
      <Header title="Controlled Items (Title III)" hint="Item and who may possess it." onAdd={add} addLabel="Add Item" testid="penal-controlled-add" />
      {items.map((it, idx) => (
        <div key={it.id} data-testid={`penal-controlled-edit-${idx}`} className="border border-[#2A201A] bg-surface/40 p-5 space-y-4">
          <Field label="Item" value={it.item} onChange={(v) => patch(it.id, "item", v)} />
          <Textarea label="Permitted Person" value={it.permitted} onChange={(v) => patch(it.id, "permitted", v)} rows={2} />
          <RowActions onSave={() => save(it)} onUp={() => move(idx, -1)} onDown={() => move(idx, 1)} onDelete={() => remove(it.id)} />
        </div>
      ))}
    </div>
  );
};

export const IllegalEditor = () => {
  const { items, patch, add, save, remove, move } = useCrud("penal-illegal", { category: "I. General Contraband", name: "New item" });
  return (
    <div className="space-y-4">
      <Header title="Illegal Items (Title III)" hint="Each item with its category heading." onAdd={add} addLabel="Add Item" testid="penal-illegal-add" />
      {items.map((it, idx) => (
        <div key={it.id} data-testid={`penal-illegal-edit-${idx}`} className="border border-[#2A201A] bg-surface/40 p-5 grid gap-4 sm:grid-cols-2 items-end">
          <Field label="Category" value={it.category} onChange={(v) => patch(it.id, "category", v)} />
          <Field label="Item name" value={it.name} onChange={(v) => patch(it.id, "name", v)} />
          <div className="sm:col-span-2"><RowActions onSave={() => save(it)} onUp={() => move(idx, -1)} onDown={() => move(idx, 1)} onDelete={() => remove(it.id)} /></div>
        </div>
      ))}
    </div>
  );
};

const SECTIONS = [{ v: "procedures", l: "Procedures, Limitations & Reliefs" }, { v: "business", l: "Employment & Business Licensure" }];
export const ProvisionsEditor = () => {
  const { items, patch, add, save, remove, move } = useCrud("penal-provisions", { section: "procedures", heading: "New section", body: "" });
  return (
    <div className="space-y-4">
      <Header title="Provisions (Title I prose)" hint="Chapter 2 & 3 text blocks. One paragraph per line." onAdd={add} addLabel="Add Block" testid="penal-prov-add" />
      {items.map((it, idx) => (
        <div key={it.id} data-testid={`penal-prov-edit-${idx}`} className="border border-[#2A201A] bg-surface/40 p-5 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#8F877C] mb-2">Chapter</p>
              <select value={it.section} onChange={(e) => patch(it.id, "section", e.target.value)}
                className="w-full bg-obsidian border border-[#2A201A] px-3 py-2.5 text-sm text-bone focus:outline-none focus:border-teal">
                {SECTIONS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
            </div>
            <Field label="Heading" value={it.heading} onChange={(v) => patch(it.id, "heading", v)} />
          </div>
          <Textarea label="Body (one paragraph per line)" value={it.body} onChange={(v) => patch(it.id, "body", v)} rows={5} />
          <RowActions onSave={() => save(it)} onUp={() => move(idx, -1)} onDown={() => move(idx, 1)} onDelete={() => remove(it.id)} />
        </div>
      ))}
    </div>
  );
};

const TONES = [
  { v: "info", l: "Info (teal)" }, { v: "notice", l: "Notice (gold)" },
  { v: "warn", l: "Warn (ember)" }, { v: "danger", l: "Danger (red)" }, { v: "severe", l: "Severe (red filled)" },
];
export const PenaltiesEditor = () => {
  const { items, patch, add, save, remove, move } = useCrud("penalties", { label: "Next Offense", detail: "", tone: "warn" });
  return (
    <div className="space-y-4">
      <Header title="Penalty Tiers" hint="The strike-tier cards shown at the bottom of the Rules page." onAdd={add} addLabel="Add Tier" testid="penalty-add" />
      {items.map((it, idx) => (
        <div key={it.id} data-testid={`penalty-edit-${idx}`} className="border border-[#2A201A] bg-surface/40 p-5 space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Label" value={it.label} onChange={(v) => patch(it.id, "label", v)} />
            <Field label="Detail" value={it.detail} onChange={(v) => patch(it.id, "detail", v)} />
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#8F877C] mb-2">Colour tone</p>
              <select value={it.tone} onChange={(e) => patch(it.id, "tone", e.target.value)}
                className="w-full bg-obsidian border border-[#2A201A] px-3 py-2.5 text-sm text-bone focus:outline-none focus:border-teal">
                {TONES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </div>
          </div>
          <RowActions onSave={() => save(it)} onUp={() => move(idx, -1)} onDown={() => move(idx, 1)} onDelete={() => remove(it.id)} />
        </div>
      ))}
    </div>
  );
};

export const WarningsEditor = () => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/site-config").then(({ data }) => setText((data.warnings || []).join("\n"))); }, []);
  const save = async () => {
    setBusy(true);
    const warnings = text.split("\n").map((s) => s.trim()).filter(Boolean);
    try { await adminApi.put("/site-config", { warnings }); toast.success("Warning phrases saved."); }
    catch { toast.error("Save failed."); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-[#2A201A] bg-surface/40 p-5 space-y-4" data-testid="warnings-editor">
      <Header title="Warning Phrases" hint="Rule lines containing any of these phrases are flagged in red. One phrase per line." />
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} data-testid="warnings-textarea"
        className="w-full bg-obsidian border border-[#2A201A] px-3 py-2 text-sm text-bone focus:outline-none focus:border-teal resize-none" />
      <button onClick={save} disabled={busy} data-testid="warnings-save"
        className="inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest bg-teal text-obsidian px-6 py-3 hover:bg-bone transition-colors disabled:opacity-50">
        <Save className="h-4 w-4" /> Save Phrases
      </button>
    </div>
  );
};

export const PenalTextEditor = () => {  const [t, setT] = useState({ illegal_exception: "", aggravating_note: "", penalty_note: "", terms_overline: "", terms_title: "", proc_overline: "", proc_title: "", biz_overline: "", biz_title: "", aggr_heading: "", aggr_badge: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/site-config").then(({ data }) => setT({ illegal_exception: "", aggravating_note: "", penalty_note: "", terms_overline: "", terms_title: "", proc_overline: "", proc_title: "", biz_overline: "", biz_title: "", aggr_heading: "", aggr_badge: "", ...(data.penal_text || {}) })); }, []);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/site-config");
      await adminApi.put("/site-config", { penal_text: { ...(data.penal_text || {}), ...t } });
      toast.success("Notes saved.");
    } catch { toast.error("Save failed."); }
    finally { setBusy(false); }
  };
  const set = (k, v) => setT((p) => ({ ...p, [k]: v }));
  return (
    <div className="border border-[#2A201A] bg-surface/40 p-5 space-y-4" data-testid="penal-text-editor">
      <Header title="Section Headings & Notes (Title I)" hint="The fixed titles, chapter overlines and small standalone lines on the Penal Code page. Leave a field blank to use the default; leave the badge blank to hide it." />
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Ch.1 overline" value={t.terms_overline} onChange={(v) => set("terms_overline", v)} placeholder="Title I · Chapter One" />
        <Field label="Ch.1 title" value={t.terms_title} onChange={(v) => set("terms_title", v)} placeholder="Defined Terms" />
        <Field label="Ch.2 overline" value={t.proc_overline} onChange={(v) => set("proc_overline", v)} placeholder="Title I · Chapter Two" />
        <Field label="Ch.2 title" value={t.proc_title} onChange={(v) => set("proc_title", v)} placeholder="Procedures, Limitations & Reliefs" />
        <Field label="Ch.3 overline" value={t.biz_overline} onChange={(v) => set("biz_overline", v)} placeholder="Title I · Chapter Three" />
        <Field label="Ch.3 title" value={t.biz_title} onChange={(v) => set("biz_title", v)} placeholder="Employment & Business Licensure" />
        <Field label="Aggravating heading" value={t.aggr_heading} onChange={(v) => set("aggr_heading", v)} placeholder="D) Aggravating Circumstances" />
        <Field label="Aggravating badge (blank = hidden)" value={t.aggr_badge} onChange={(v) => set("aggr_badge", v)} placeholder="Under Review" />
      </div>
      <Textarea label="Aggravating note (Title I)" value={t.aggravating_note} onChange={(v) => set("aggravating_note", v)} rows={2} />
      <Textarea label="Illegal items exception (Title III)" value={t.illegal_exception} onChange={(v) => set("illegal_exception", v)} rows={2} />
      <Textarea label="Penalty grid note (Rules page)" value={t.penalty_note} onChange={(v) => set("penalty_note", v)} rows={2} />
      <button onClick={save} disabled={busy} data-testid="penal-text-save"
        className="inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest bg-teal text-obsidian px-6 py-3 hover:bg-bone transition-colors disabled:opacity-50">
        <Save className="h-4 w-4" /> Save Notes
      </button>
    </div>
  );
};

const BIZ_GROUPS = [{ v: "ownership", l: "Ownership & Family" }, { v: "sales_intro", l: "Sales Guide — shared rules" }];
const BIZ_KINDS = [{ v: "prose", l: "Prose (paragraphs)" }, { v: "denylist", l: "Deny list (red ✕ items)" }];
export const BizPolicyEditor = () => {
  const { items, patch, add, save, remove, move } = useCrud("biz-policy", { group: "ownership", kind: "prose", heading: "New section", body: "" });
  return (
    <div className="space-y-4">
      <Header title="Business Policy Text" hint="Ownership & Family sections and the Sales Guide shared rules. One paragraph per line." onAdd={add} addLabel="Add Section" testid="biz-policy-add" />
      {items.map((it, idx) => (
        <div key={it.id} data-testid={`biz-policy-edit-${idx}`} className="border border-[#2A201A] bg-surface/40 p-5 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#8F877C] mb-2">Group</p>
              <select value={it.group} onChange={(e) => patch(it.id, "group", e.target.value)} className="w-full bg-obsidian border border-[#2A201A] px-3 py-2.5 text-sm text-bone focus:outline-none focus:border-teal">
                {BIZ_GROUPS.map((g) => <option key={g.v} value={g.v}>{g.l}</option>)}
              </select>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#8F877C] mb-2">Style</p>
              <select value={it.kind} onChange={(e) => patch(it.id, "kind", e.target.value)} className="w-full bg-obsidian border border-[#2A201A] px-3 py-2.5 text-sm text-bone focus:outline-none focus:border-teal">
                {BIZ_KINDS.map((k) => <option key={k.v} value={k.v}>{k.l}</option>)}
              </select>
            </div>
          </div>
          <Field label="Heading" value={it.heading} onChange={(v) => patch(it.id, "heading", v)} />
          <Textarea label="Body (one paragraph/item per line)" value={it.body} onChange={(v) => patch(it.id, "body", v)} rows={5} />
          <RowActions onSave={() => save(it)} onUp={() => move(idx, -1)} onDown={() => move(idx, 1)} onDelete={() => remove(it.id)} />
        </div>
      ))}
    </div>
  );
};

export const BizSalesEditor = () => {
  const { items, patch, add, save, remove, move } = useCrud("biz-sales", { business: "New Business", may_sell: "", cannot_sell: "", notes: "" });
  return (
    <div className="space-y-4">
      <Header title="Sales Guide — Per Business" hint="What each business may and may not sell. One item per line." onAdd={add} addLabel="Add Business" testid="biz-sales-add" />
      {items.map((it, idx) => (
        <div key={it.id} data-testid={`biz-sales-edit-${idx}`} className="border border-[#2A201A] bg-surface/40 p-5 space-y-4">
          <Field label="Business" value={it.business} onChange={(v) => patch(it.id, "business", v)} />
          <div className="grid gap-4 sm:grid-cols-2">
            <Textarea label="May Sell (one per line)" value={it.may_sell} onChange={(v) => patch(it.id, "may_sell", v)} rows={6} />
            <Textarea label="Cannot Sell (one per line)" value={it.cannot_sell} onChange={(v) => patch(it.id, "cannot_sell", v)} rows={6} />
          </div>
          <Textarea label="Exceptions / Notes" value={it.notes} onChange={(v) => patch(it.id, "notes", v)} rows={2} />
          <RowActions onSave={() => save(it)} onUp={() => move(idx, -1)} onDown={() => move(idx, 1)} onDelete={() => remove(it.id)} />
        </div>
      ))}
    </div>
  );
};

export const HomeEditor = () => {
  const [h, setH] = useState({ hero_line1: "", hero_highlight: "", hero_line3: "", hero_sub: "", discord_sub: "", cta_heading: "", creed_label: "", creed: [], features_label: "", features_heading: "", features: [] });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/site-config").then(({ data }) => setH({ hero_line1: "", hero_highlight: "", hero_line3: "", hero_sub: "", discord_sub: "", cta_heading: "", creed_label: "", creed: [], features_label: "", features_heading: "", features: [], ...(data.home || {}) })); }, []);
  const set = (k, v) => setH((p) => ({ ...p, [k]: v }));
  const setItem = (key, idx, field, v) => setH((p) => ({ ...p, [key]: p[key].map((it, i) => (i === idx ? { ...it, [field]: v } : it)) }));
  const save = async () => {
    setBusy(true);
    try { await adminApi.put("/site-config", { home: h }); toast.success("Home page saved."); }
    catch { toast.error("Save failed."); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-8" data-testid="home-editor">
      <Header title="Home Page" hint="Edit the hero, Creed, feature cards, Discord blurb and closing call-to-action shown on the landing page." />
      <div className="border border-[#2A201A] bg-surface/40 p-5 space-y-4">
        <p className="font-feature text-sm uppercase tracking-[0.2em] text-teal">Hero</p>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Line 1" value={h.hero_line1} onChange={(v) => set("hero_line1", v)} />
          <Field label="Highlight (teal)" value={h.hero_highlight} onChange={(v) => set("hero_highlight", v)} />
          <Field label="Line 3" value={h.hero_line3} onChange={(v) => set("hero_line3", v)} />
        </div>
        <Textarea label="Sub-heading" value={h.hero_sub} onChange={(v) => set("hero_sub", v)} rows={2} />
      </div>

      <div className="border border-[#2A201A] bg-surface/40 p-5 space-y-4">
        <p className="font-feature text-sm uppercase tracking-[0.2em] text-teal">The Creed (Section 001)</p>
        <Field label="Section label" value={h.creed_label} onChange={(v) => set("creed_label", v)} />
        {(h.creed || []).map((c, idx) => (
          <div key={idx} data-testid={`home-creed-edit-${idx}`} className="border border-[#2A201A] bg-obsidian/40 p-4 space-y-3">
            <Field label={`Entry ${c.n || idx + 1} — Title`} value={c.head} onChange={(v) => setItem("creed", idx, "head", v)} />
            <Textarea label="Description" value={c.body} onChange={(v) => setItem("creed", idx, "body", v)} rows={3} />
          </div>
        ))}
      </div>

      <div className="border border-[#2A201A] bg-surface/40 p-5 space-y-4">
        <p className="font-feature text-sm uppercase tracking-[0.2em] text-teal">What Sets Us Apart (Section 002)</p>
        <Field label="Section label" value={h.features_label} onChange={(v) => set("features_label", v)} />
        <Textarea label="Heading" value={h.features_heading} onChange={(v) => set("features_heading", v)} rows={2} />
        <div className="grid gap-4 sm:grid-cols-2">
          {(h.features || []).map((f, idx) => (
            <div key={idx} data-testid={`home-feature-edit-${idx}`} className="border border-[#2A201A] bg-obsidian/40 p-4 space-y-3">
              <Field label={`Card ${f.n || idx + 1} — Title`} value={f.title} onChange={(v) => setItem("features", idx, "title", v)} />
              <Textarea label="Description" value={f.body} onChange={(v) => setItem("features", idx, "body", v)} rows={3} />
            </div>
          ))}
        </div>
      </div>

      <div className="border border-[#2A201A] bg-surface/40 p-5 space-y-4">
        <p className="font-feature text-sm uppercase tracking-[0.2em] text-teal">Discord Band</p>
        <Textarea label="Description" value={h.discord_sub} onChange={(v) => set("discord_sub", v)} rows={2} />
      </div>
      <div className="border border-[#2A201A] bg-surface/40 p-5 space-y-4">
        <p className="font-feature text-sm uppercase tracking-[0.2em] text-teal">Closing Call-to-Action</p>
        <Textarea label="Heading (use a new line for a line break)" value={h.cta_heading} onChange={(v) => set("cta_heading", v)} rows={2} />
      </div>
      <button onClick={save} disabled={busy} data-testid="home-save"
        className="inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest bg-teal text-obsidian px-6 py-3 hover:bg-bone transition-colors disabled:opacity-50">
        <Save className="h-4 w-4" /> Save Home Page
      </button>
    </div>
  );
};
